/*
 * Heritage Olive quick add (13 Oct 2026).
 * <heritage-quick-add> is the size strip inside a product card's image (snippets/heritage-quick-add.liquid).
 * A tap on a size POSTs that variant to the cart, then renders the returned cart-drawer and cart-icon-bubble sections
 * into the drawer and opens it, the same way assets/product-form.js and assets/knit-duo.js do.
 * Loaded with defer by sections/main-collection-product-grid.liquid only.
 */
if (!customElements.get('heritage-quick-add')) {
	customElements.define(
		'heritage-quick-add',
		class HeritageQuickAdd extends HTMLElement {
			constructor() {
				super();
				this.onClick = this.onClick.bind(this);
				this.onOutside = this.onOutside.bind(this);
				this.onKeyUp = this.onKeyUp.bind(this);
			}

			connectedCallback() {
				this.toggleButton = this.querySelector('.ho-qa__toggle');
				this.status = this.querySelector('.ho-qa__status');
				this.addEventListener('click', this.onClick);
				this.addEventListener('keyup', this.onKeyUp);
			}

			disconnectedCallback() {
				this.removeEventListener('click', this.onClick);
				this.removeEventListener('keyup', this.onKeyUp);
				document.removeEventListener('click', this.onOutside);
			}

			onClick(event) {
				const toggle = event.target.closest('.ho-qa__toggle');
				if (toggle && this.contains(toggle)) {
					event.preventDefault();
					event.stopPropagation();
					this.setOpen(!this.classList.contains('is-open'));
					return;
				}
				const size = event.target.closest('.ho-qa__size');
				if (!size || !this.contains(size)) return;
				event.preventDefault();
				event.stopPropagation();
				if (size.disabled || !size.dataset.variantId) return;
				this.add(size);
			}

			onKeyUp(event) {
				if (event.code === 'Escape' && this.classList.contains('is-open')) {
					this.setOpen(false);
					if (this.toggleButton) this.toggleButton.focus();
				}
			}

			onOutside(event) {
				if (!this.contains(event.target)) this.setOpen(false);
			}

			setOpen(open) {
				this.classList.toggle('is-open', open);
				if (this.toggleButton) this.toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
				if (open) {
					document.addEventListener('click', this.onOutside);
				} else {
					document.removeEventListener('click', this.onOutside);
				}
			}

			announce(text) {
				if (this.status) this.status.textContent = text;
			}

			async add(button) {
				if (button.getAttribute('aria-disabled') === 'true') return;
				button.setAttribute('aria-disabled', 'true');
				button.classList.add('is-busy');

				const id = Number(button.dataset.variantId);
				if (!button.dataset.hoLabel) button.dataset.hoLabel = button.textContent.trim();
				const original = button.dataset.hoLabel;
				const config = fetchConfig('javascript');
				config.headers['X-Requested-With'] = 'XMLHttpRequest';
				config.body = JSON.stringify({
					items: [{ id, quantity: 1 }],
					sections: 'cart-drawer,cart-icon-bubble',
					sections_url: window.location.pathname,
				});

				const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

				try {
					const response = await fetch(`${window.routes.cart_add_url}.js`, config);
					const data = await response.json();
					if (!response.ok || data.status) throw new Error(data.description || data.message || 'add failed');

					document.dispatchEvent(
						new CustomEvent('variant:add', {
							detail: {
								variant: { id: String(id) },
								quantity: 1,
								formElement: null,
								sectionId: null,
							},
						})
					);
					if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
						publish(PUB_SUB_EVENTS.cartUpdate, { source: 'heritage-quick-add', productVariantId: id });
					}
					fetch(`${window.routes.cart_url}.js`)
						.then((cartResponse) => cartResponse.json())
						.then((parsedState) => {
							document.dispatchEvent(
								new CustomEvent('cart:change', { detail: { cart: parsedState, sectionId: null } })
							);
						})
						.catch(() => {});

					this.showState(button, original, this.dataset.addedLabel || original, 'is-added');
					this.announce(
						(button.getAttribute('aria-label') || '') + ' ' + (this.dataset.addedLabel || '')
					);

					if (cart && data.sections && typeof cart.renderContents === 'function') {
						if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
						cart.renderContents({ id, sections: data.sections });
					} else {
						window.location.href = window.routes.cart_url;
					}
				} catch (error) {
					console.error('Heritage quick add failed', error);
					this.showState(button, original, this.dataset.errorLabel || original, 'is-error');
				} finally {
					button.removeAttribute('aria-disabled');
					button.classList.remove('is-busy');
				}
			}

			showState(button, original, label, className) {
				window.clearTimeout(button.__hoTimer);
				button.textContent = label;
				button.classList.add(className);
				button.__hoTimer = window.setTimeout(() => {
					button.textContent = original;
					button.classList.remove('is-added', 'is-error');
				}, 1500);
			}
		}
	);
}
